import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal, SectionLabel, useScrollTo, EASE, openSchedule } from "./Reveal";

const SERVICES = [
  { n: "01", title: "FOOD CONCEPT & MENU STRATEGY", copy: "Helping translate a food concept into a commercially workable menu.", img: "/assets/img/food-premium.jpg" },
  { n: "02", title: "KITCHEN PLANNING & DESIGN", copy: "Planning kitchen layouts around actual production requirements, workflow and capacity.", img: "/assets/img/blueprint.jpg" },
  { n: "03", title: "MANPOWER PLANNING", copy: "Understanding staffing requirements based on menu complexity, volume and operating model.", img: "/assets/img/skope-kitchen-4.jpg" },
  { n: "04", title: "EQUIPMENT PLANNING", copy: "Identifying the equipment required based on actual kitchen requirements.", img: "/assets/img/hero-kitchen.jpg" },
  { n: "05", title: "WORKFLOW & OPERATIONAL EFFICIENCY", copy: "Reducing unnecessary movement, bottlenecks and operational inefficiencies.", img: "/assets/img/skope-kitchen-3.jpg" },
  { n: "06", title: "COMMERCIAL KITCHEN PLANNING", copy: "Understanding the commercial implications of kitchen decisions.", img: "/assets/img/skope-kitchen-1.jpg" },
  { n: "07", title: "SCALABILITY & STANDARDISATION", copy: "Creating kitchen systems that can be replicated across locations.", img: "/assets/img/skope-kitchen-2.jpg" },
  { n: "08", title: "PRE-OPENING SUPPORT", copy: "Helping founders make important operational decisions before committing significant capital.", img: "/assets/img/chef-plating.jpg" },
];

const Services = () => {
  const [active, setActive] = useState(0);
  const scrollTo = useScrollTo();

  return (
    <section id="services" className="relative bg-white py-28 lg:py-40" data-testid="services-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <Reveal><SectionLabel>Services — 06</SectionLabel></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-display font-semibold text-3xl lg:text-5xl tracking-tight text-ink leading-[1.08]" data-testid="services-headline">
            STRATEGY BEFORE STAINLESS STEEL.
          </h2>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            {SERVICES.map((s, i) => (
              <Reveal key={s.n} delay={0.04 * i}>
                <button
                  onMouseEnter={() => setActive(i)}
                  onClick={() => scrollTo("#contact")}
                  className="group w-full text-left border-b border-ink/10 py-6 lg:py-7 flex items-start gap-6 hover:border-skope/50 transition-colors duration-500"
                  data-testid={`service-${s.n}`}
                >
                  <span className="font-mono text-xs text-skope/70 pt-1.5">{s.n}</span>
                  <span className="flex-1">
                    <span className="block font-display font-semibold text-lg lg:text-2xl tracking-tight text-ink/85 group-hover:text-skope transition-colors duration-400">
                      {s.title}
                    </span>
                    <span className="block mt-2 text-sm text-ink/45 leading-relaxed max-w-lg">{s.copy}</span>
                  </span>
                  <ArrowUpRight size={18} className="mt-1.5 text-ink/20 group-hover:text-skope group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-500 shrink-0" />
                </button>
              </Reveal>
            ))}
          </div>

          <div className="hidden lg:block lg:col-span-5">
            <div className="sticky top-32 relative border border-ink/10 h-[560px] overflow-hidden" data-testid="services-image-panel">
              <motion.img
                key={active}
                src={SERVICES[active].img}
                alt={SERVICES[active].title}
                initial={{ opacity: 0, scale: 1.06 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, ease: EASE }}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-white/80 via-transparent to-transparent" />
              <div className="absolute -left-px -top-px w-6 h-6 border-l-2 border-t-2 border-skope" />
              <div className="absolute bottom-5 left-5 right-5 flex justify-between items-end">
                <span className="font-mono text-[10px] tracking-[0.25em] text-ink/70 uppercase">{SERVICES[active].title}</span>
                <span className="font-mono text-[10px] text-skope">[ {SERVICES[active].n} ]</span>
              </div>
            </div>
          </div>

          <Reveal delay={0.2}>
            <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <button
                onClick={() => openSchedule()}
                className="group flex items-center justify-center gap-3 bg-skope text-ink font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-ink hover:text-white transition-colors duration-300"
                data-testid="services-schedule-cta"
              >
                Schedule a Call
                <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-300" />
              </button>
              <p className="font-mono text-[11px] tracking-[0.15em] text-ink/40 uppercase">
                For a direct conversation about your food business.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Services;
